import { useQuery } from "@tanstack/react-query";
import {
  ProductDetailDrawerRequestDto,
  QuotesClient,
  ProductDetailDrawerDto,
} from "@lib/ShiOneClient";
import { useApi } from "@utils/useApi";
import { useClaims } from "../../../../../auth/ClaimsProvider";
import { ShiOnePermission } from "../../../../../auth/authorizationTypes";
import ms from "ms";

export interface QuoteProductDetailDrawerHookResult {
  data: ProductDetailDrawerDto | null;
  isLoading: boolean;
  isInitialLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  isFetched: boolean;
}

// The drawer service only renders these classes; quote items default to commodity.
const SUPPORTED_PRODUCT_CLASSES = ["shicommodity", "shiconfigsystem"];

/**
 * Fetches enriched product-detail-drawer data for a product on a quote, via the quote-permissioned
 * endpoint (so it doesn't borrow the standards/orders permission gates). Mirrors
 * `useOrderProductDetailDrawerHook`: enabled only for quotes viewers, a valid request, and a
 * supported product class.
 */
export default function useQuoteProductDetailDrawer(
  request: ProductDetailDrawerRequestDto | null,
): QuoteProductDetailDrawerHookResult {
  const api = useApi(QuotesClient);
  const { hasPermission } = useClaims();
  const canViewQuotes =
    hasPermission(ShiOnePermission.quoteViewMy) ||
    hasPermission(ShiOnePermission.quoteViewOrgUnit);

  const isSupportedProductClass =
    request?.productClass != null &&
    SUPPORTED_PRODUCT_CLASSES.includes(request.productClass.toLowerCase());

  const fetchProductDetailDrawer = async (): Promise<ProductDetailDrawerDto | null> => {
    if (!request) return null;
    return await api.getQuoteProductDetailDrawer(request);
  };

  const productKey = {
    productDescriptor: request?.productDescriptor,
    productClass: request?.productClass,
    orgunitId: request?.orgunitId,
  };

  const query = useQuery({
    queryKey: ["quote-product-detail-drawer", productKey],
    queryFn: fetchProductDetailDrawer,
    enabled:
      canViewQuotes &&
      request !== null &&
      request.productDescriptor != null &&
      request.orgunitId != null &&
      isSupportedProductClass,
    staleTime: ms("1m"),
    retry: false,
  });

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isInitialLoading: query.isInitialLoading,
    isError: query.isError,
    isFetching: query.isFetching,
    isFetched: query.isFetched,
  };
}
